import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { updateTokensAction } from '@/redux/slicers/auth.slice';
import {
  beginEntityBootstrap,
  completeEntityBootstrap,
  setCurrentEntity,
  setCurrentEntityApplication,
  setEntities,
  setEntityApplications,
  setEntityContextError,
  setEntitySelectionRequired,
  setEntitySwitching,
} from '@/redux/slicers/entity-context.slice';
import { useRootSelector } from '@/hooks/selector.hook';
import {
  chooseCurrentApplication,
  getLastActiveEntityId,
  tokenHasEntityContext,
  useEntityContextService,
} from '@/services/entity-context.service';
import type { EntityApplication } from '@/types/entity-context';

let bootstrapPromise: Promise<void> | null = null;
let createApplicationPromise: Promise<EntityApplication | null> | null = null;

export function useEntityContext() {
  const dispatch = useDispatch();
  const state = useRootSelector((root) => root.entityContext);
  const {
    createEntityApplication,
    getCurrentEntityContext,
    listMyEntities,
    listMyEntityApplications,
    switchActiveEntity,
  } = useEntityContextService();

  const switchEntity = useCallback(
    async (entityId: string) => {
      dispatch(setEntitySwitching(true));
      dispatch(setEntityContextError(null));
      try {
        const result = await switchActiveEntity(entityId);
        if (!result) throw new Error('Unable to switch entity.');
        dispatch(updateTokensAction({ token: result.accessToken }));
        dispatch(setCurrentEntity(result.context));
        dispatch(setEntitySelectionRequired(false));
        return result.context;
      } catch {
        dispatch(setEntityContextError('Unable to select this business. Please try again.'));
        return null;
      } finally {
        dispatch(setEntitySwitching(false));
      }
    },
    [dispatch, switchActiveEntity],
  );

  const initialize = useCallback(async () => {
    if (bootstrapPromise) return bootstrapPromise;

    bootstrapPromise = (async () => {
      dispatch(beginEntityBootstrap());
      try {
        const entities = await listMyEntities();
        dispatch(setEntities(entities));

        let current = null;
        if (tokenHasEntityContext()) {
          try {
            current = await getCurrentEntityContext();
          } catch {
            // A stale or removed membership is handled below like an unselected Entity.
          }
        }

        if (current && entities.some((entity) => entity.id === current?.entityId)) {
          dispatch(setCurrentEntity(current));
          dispatch(setEntitySelectionRequired(false));
          try {
            const applications = await listMyEntityApplications();
            dispatch(setEntityApplications(applications));
            dispatch(setCurrentEntityApplication(chooseCurrentApplication(applications)));
          } catch {
            // Application history is optional once the Entity workspace exists.
          }
          return;
        }

        const rememberedId = getLastActiveEntityId();
        const target =
          entities.find((entity) => entity.id === rememberedId) ??
          (entities.length === 1 ? entities[0] : null);

        if (target) {
          const result = await switchActiveEntity(target.id);
          if (!result) throw new Error('Unable to switch entity.');
          dispatch(updateTokensAction({ token: result.accessToken }));
          dispatch(setCurrentEntity(result.context));
          try {
            const applications = await listMyEntityApplications();
            dispatch(setEntityApplications(applications));
            dispatch(setCurrentEntityApplication(chooseCurrentApplication(applications)));
          } catch {
            // Application history is optional once the Entity workspace exists.
          }
          return;
        }

        dispatch(setCurrentEntity(null));
        dispatch(setEntitySelectionRequired(entities.length > 1));

        if (entities.length === 0) {
          const applications = await listMyEntityApplications();
          dispatch(setEntityApplications(applications));
          dispatch(setCurrentEntityApplication(chooseCurrentApplication(applications)));
        }
      } catch {
        dispatch(setEntityContextError('Unable to load your businesses. Please refresh and try again.'));
      } finally {
        dispatch(completeEntityBootstrap());
        bootstrapPromise = null;
      }
    })();

    return bootstrapPromise;
  }, [dispatch, getCurrentEntityContext, listMyEntities, listMyEntityApplications, switchActiveEntity]);

  const ensureEntityApplication = useCallback(async () => {
    if (state.currentApplication) return state.currentApplication;
    if (!createApplicationPromise) createApplicationPromise = createEntityApplication();

    try {
      const application = await createApplicationPromise;
      if (!application) throw new Error('Unable to create entity application.');
      dispatch(setCurrentEntityApplication(application));
      return application;
    } catch (error) {
      dispatch(setEntityContextError('Unable to start business verification. Please try again.'));
      throw error;
    } finally {
      createApplicationPromise = null;
    }
  }, [createEntityApplication, dispatch, state.currentApplication]);

  const startNewEntityApplication = useCallback(async (draftName: string | null = null) => {
    const openApplication = state.applications.find(
      (application) => application.status !== 'Approved' && !application.entityId,
    );
    if (openApplication) {
      dispatch(setCurrentEntityApplication(openApplication));
      dispatch(setEntityContextError(null));
      return openApplication;
    }

    if (!createApplicationPromise) createApplicationPromise = createEntityApplication(draftName);

    try {
      const application = await createApplicationPromise;
      if (!application) throw new Error('Unable to create entity application.');
      dispatch(setCurrentEntityApplication(application));
      dispatch(setEntityContextError(null));
      return application;
    } catch (error) {
      dispatch(setEntityContextError('Unable to open a new business account. Please try again.'));
      throw error;
    } finally {
      createApplicationPromise = null;
    }
  }, [createEntityApplication, dispatch, state.applications]);

  const refreshApplications = useCallback(async () => {
    const applications = await listMyEntityApplications();
    const currentApplication = chooseCurrentApplication(applications);
    dispatch(setEntityApplications(applications));
    dispatch(setCurrentEntityApplication(currentApplication));
    return currentApplication;
  }, [dispatch, listMyEntityApplications]);

  const refreshEntities = useCallback(async () => {
    const entities = await listMyEntities();
    dispatch(setEntities(entities));
    return entities;
  }, [dispatch, listMyEntities]);

  return {
    ...state,
    initialize,
    switchEntity,
    ensureEntityApplication,
    startNewEntityApplication,
    refreshApplications,
    refreshEntities,
  };
}
